import React, {useEffect, useState} from "react";
import {Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import {
	RequiredDocumentCell,
	RequiredDocumentCellStatusEnum,
	RequiredDocumentRow,
	RequiredDocumentsDto,
	RequiredDocumentsRestControllerApiFactory,
	RequiredDocumentType,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {
	EmptyState,
	ListCard,
	Page,
	PageHeader,
	Panel,
	PersonAvatar,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	tableContainerSx,
	tableSx,
	Tone,
} from "../../../../components/kit";
import {useComplianceAccess} from "../access";

const STATUS: Record<string, { label: string, tone: Tone }> = {
	[RequiredDocumentCellStatusEnum.OK]: {label: "Lo tiene", tone: "success"},
	[RequiredDocumentCellStatusEnum.EXPIRED]: {label: "Caducado", tone: "error"},
	[RequiredDocumentCellStatusEnum.MISSING]: {label: "Falta", tone: "warning"},
	[RequiredDocumentCellStatusEnum.NOT_DUE]: {label: "Aún no le toca", tone: "neutral"},
};

/** En los periódicos (#14415), lo que falta dice de qué periodo: «Falta · septiembre de 2026». */
const CellChip = ({cell}: { cell?: RequiredDocumentCell }) => {
	const s = STATUS[cell?.status || RequiredDocumentCellStatusEnum.MISSING];
	const label = cell?.status === RequiredDocumentCellStatusEnum.MISSING && cell.period
		? `${s.label} · ${cell.period}`
		: cell?.expiration && cell.status !== RequiredDocumentCellStatusEnum.MISSING
			? `${s.label} · ${dateToString(cell.expiration)}` : s.label;
	return <SoftChip tone={s.tone} label={label}/>;
};

/** «Hoja de actividad (cada mes: septiembre de 2026)»; los de una vez, solo el nombre. */
const typeLabel = (t: RequiredDocumentType) => t.duePeriod
	? `${t.name} (${t.period === "MONTHLY" ? "cada mes" : "cada año"}: ${t.duePeriod})` : t.name;

const prevention = (r: RequiredDocumentRow) => r.preventionTrainingDate
	? <Typography variant="body2" sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(r.preventionTrainingDate)}</Typography>
	: <Typography variant="body2" color="text.disabled">Sin fecha</Typography>;

/**
 * Documentos obligatorios de la plantilla activa (#13992): quién tiene cada uno, a quién le falta y a
 * quién le ha caducado. Es la evidencia del alta, no sustituye a sus casillas en Redmine. La fecha de
 * la formación en prevención sale aparte porque se lleva en la ficha, no como documento.
 */
export const RequiredDocumentsPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(RequiredDocumentsRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const navigate = useNavigate();
	const access = useComplianceAccess();
	const [data, setData] = useState<RequiredDocumentsDto>();
	const [error, setError] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.reportRequiredDocuments()).then(setData).catch(() => setError(true));
	}, []);

	const types = data?.types || [];
	const rows = data?.rows || [];
	const missing = rows.reduce((n, r) => n + (r.missing || 0), 0);
	const expired = rows.reduce((n, r) => n + (r.expired || 0), 0);
	// Abrir los documentos de la persona es de RR. HH.; quien solo es de compliance ve el cuadro.
	const open = access.hr ? (r: RequiredDocumentRow) => navigate(`/app/admin/employee/person/${r.personId}/document`) : undefined;
	const cellOf = (r: RequiredDocumentRow, typeId?: number) => r.cells?.find(c => c.typeId === typeId);

	return (
		<Page>
			<PageHeader title="Documentación de la plantilla"
						subtitle="Los documentos de persona marcados como obligatorios para toda la plantilla activa, y quién los tiene."/>
			<StatGrid>
				<StatTile loading={!data && !error} icon={<CheckCircleOutlineRoundedIcon/>} tone="success" label="Personas al día"
						  value={data ? `${data.complete} de ${rows.length}` : "—"}/>
				<StatTile loading={!data && !error} icon={<ErrorOutlineRoundedIcon/>} tone="warning" label="Documentos que faltan"
						  value={missing}/>
				<StatTile loading={!data && !error} icon={<EventBusyOutlinedIcon/>} tone="error" label="Documentos caducados"
						  value={expired}/>
			</StatGrid>

			<Panel title="Por persona"
				   subtitle={types.length ? `Obligatorios: ${types.map(typeLabel).join(", ")}.` : undefined}>
				{error ? (
					<EmptyState icon={<ErrorOutlineRoundedIcon/>} title="No se ha podido calcular"
								description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
				) : !data ? <SkeletonRows rows={6}/> : types.length === 0 ? (
					<EmptyState icon={<AssignmentIndOutlinedIcon/>} title="Ningún documento es obligatorio todavía"
								description="Se marca en RR. HH. › Tipos de Documento, con «Obligatorio para toda la plantilla»."/>
				) : compact ? (
					<Stack spacing={1.5}>
						{rows.map(r => (
							<ListCard key={r.personId} onClick={open && (() => open(r))}>
								<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
									<PersonAvatar name={r.name} size={28}/>
									<Typography variant="body2" fontWeight={600} sx={{minWidth: 0}} noWrap>{r.name}</Typography>
								</Stack>
								<Stack spacing={0.75} sx={{mt: 1.25}}>
									{types.map(t => (
										<Stack key={t.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
											<Typography variant="caption" color="text.secondary" sx={{minWidth: 0}}>{typeLabel(t)}</Typography>
											<CellChip cell={cellOf(r, t.id)}/>
										</Stack>
									))}
									<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
										<Typography variant="caption" color="text.secondary">Formación en prevención</Typography>
										{prevention(r)}
									</Stack>
								</Stack>
							</ListCard>
						))}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Persona</TableCell>
									{types.map(t => <TableCell key={t.id}>{typeLabel(t)}</TableCell>)}
									<TableCell>Formación en prevención</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map(r => (
									<TableRow key={r.personId} hover={!!open} onClick={open && (() => open(r))}
											  sx={{cursor: open ? "pointer" : undefined}}>
										<TableCell>
											<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
												<PersonAvatar name={r.name} size={28}/>
												<Typography variant="body2" fontWeight={600} noWrap>{r.name}</Typography>
											</Stack>
										</TableCell>
										{types.map(t => <TableCell key={t.id}><CellChip cell={cellOf(r, t.id)}/></TableCell>)}
										<TableCell>{prevention(r)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
				<Box sx={{mt: 2}}>
					<Typography variant="caption" color="text.secondary">
						Un documento con la fecha de caducidad pasada cuenta como caducado. En los que se piden cada mes
						o cada año solo cuenta el del último mes o año cerrado (o uno posterior), por su año y mes. Acusar la lectura de un documento
						en el portal no es firmarlo: aquí solo cuenta el documento subido a la persona.
					</Typography>
				</Box>
			</Panel>
		</Page>
	);
};
